/**
 * §P.4 · Un recibo, con sus líneas y sus cobros.
 *
 * **Es la pantalla de «esta familia llama porque no entiende su recibo».** Así que
 * se enseña entero: una línea por hijo, una por descuento, y los cobros anotados con
 * su referencia. Un total a pelo no se puede defender por teléfono.
 *
 * Cuatro reglas del contrato que cambian lo que se ve:
 *
 * **Sin `amount_cents` se cobra lo pendiente**, que es el 95 % de las veces. El campo
 * del importe está escondido detrás de «cobró otra cantidad»: cada número tecleado a
 * mano es un número que puede escribirse mal, y aquí escribirlo mal es cobrar de
 * menos a una familia y no enterarse.
 *
 * **Un cobro parcial no salda el recibo.** Sigue debiéndose algo y sigue apareciendo
 * en morosos, así que la pantalla dice cuánto queda y no «pagado».
 *
 * **Anular no borra, y pide motivo.** Dentro de cuatro años alguien va a preguntar
 * por qué, y «no aparece» no es una respuesta. Con cobros anotados responde `409
 * invoice_has_payments`: si el dinero entró, lo que corresponde es una devolución
 * —fase 2— y no hacer desaparecer el recibo.
 *
 * **El método lo elige el club; el proveedor lo deduce el servidor.** No se manda
 * `provider`: un Bizum cobrado por Stripe no significa nada y ensucia la
 * contabilidad para siempre.
 */
import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { api, opcionesDeSesion } from '@/shared/api/client';
import { abrirDocumento } from '@/shared/api/descargas';
import { aProblema, ErrorDeApi, type Problema } from '@/shared/api/errores';
import { Cargando, Error as PanelError } from '@/shared/ui/Estados';
import { fechaParaInput, euros, fecha, fechaYHora } from '@/shared/ui/formato';
import { aCentimos } from '@/club/cuotas/tipos';
import { EstadoDeRecibo } from '@/club/facturacion/ListaDeRecibos';
import {
  etiquetaDeMetodo,
  etiquetaDePeriodo,
  METODOS_A_MANO,
  pendienteDe,
  quedaPorDevolver,
  sePuedeDevolver,
  type Cobro,
  type DetalleDeRecibo,
  type Devolucion,
  type MetodoDeCobro,
} from '@/club/facturacion/tipos';

type Haciendo = 'nada' | 'cobrar' | 'anular';

export function FichaDeRecibo({
  club,
  reciboId,
  puedeCobrar,
  alCerrar,
  alAbrirPagador,
}: {
  club: string;
  reciboId: string;
  puedeCobrar: boolean;
  alCerrar: () => void;
  alAbrirPagador: (pagadorId: string) => void;
}) {
  const cache = useQueryClient();
  const [haciendo, setHaciendo] = useState<Haciendo>('nada');
  const [metodo, setMetodo] = useState<MetodoDeCobro>('transfer');
  const [otraCantidad, setOtraCantidad] = useState(false);
  const [importe, setImporte] = useState('');
  const [referencia, setReferencia] = useState('');
  /*
   * **Cuándo entró el dinero, que no es cuándo se teclea.** Un Bizum del viernes se
   * anota el lunes, y sin esta fecha «Lo que ha entrado» sale con «sin fecha» y no
   * se puede cuadrar con el extracto del banco, que es lo único para lo que existe
   * esa pantalla.
   *
   * El contrato ya aceptaba `charge_date` desde el principio; este formulario no lo
   * mandaba. Se vio al construir la lista de cobros, el 18-09-2026: la columna de la
   * fecha salía vacía en todas las filas.
   *
   * Por defecto hoy, que es el caso mayoritario —se cobra y se anota— y el que no
   * hay que teclear.
   */
  const [cuando, setCuando] = useState(() => fechaParaInput(new Date()));
  const [motivo, setMotivo] = useState('');
  const [problema, setProblema] = useState<Problema | null>(null);
  const [conCobros, setConCobros] = useState<Problema | null>(null);

  const detalle = useQuery({
    queryKey: ['recibo', club, reciboId],
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .invoices({ invoiceId: reciboId })
        .get();
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as DetalleDeRecibo;
    },
  });

  const refrescar = () => {
    void cache.invalidateQueries({ queryKey: ['recibo', club, reciboId] });
    void cache.invalidateQueries({ queryKey: ['recibos', club] });
    void cache.invalidateQueries({ queryKey: ['morosos', club] });
  };

  const cobrar = useMutation({
    mutationFn: async () => {
      let centimos: number | undefined;

      if (otraCantidad) {
        const parseado = aCentimos(importe);
        if (parseado === null) {
          throw new ErrorDeApi({
            title: 'El importe no se entiende',
            status: 400,
            detail: 'En euros, con dos decimales como mucho: 25 o 25,50.',
            errors: [{ field: 'amount_cents', message: 'No es un importe' }],
          });
        }
        centimos = parseado;
      }

      const r = await api.v1
        .clubs({ club })
        .invoices({ invoiceId: reciboId })
        .payments.post(
          {
            method: metodo,
            ...(centimos === undefined ? {} : { amount_cents: centimos }),
            reference: referencia.trim() === '' ? null : referencia.trim(),
            charge_date: cuando === '' ? null : cuando,
          },
          // Clave nueva por pulsación: una fija replicaría el cobro anterior y
          // diría que entró dinero que no entró.
          { headers: { 'idempotency-key': crypto.randomUUID() } }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setHaciendo('nada');
      setOtraCantidad(false);
      setImporte('');
      setReferencia('');
      refrescar();
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const anular = useMutation({
    mutationFn: async () => {
      const r = await api.v1
        .clubs({ club })
        .invoices({ invoiceId: reciboId })
        .void.post(
          { reason: motivo.trim() === '' ? null : motivo.trim() },
          { headers: { 'idempotency-key': crypto.randomUUID() } }
        );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setHaciendo('nada');
      setMotivo('');
      refrescar();
    },
    onError: (e) => {
      const p = aProblema(e);
      if (p.code === 'invoice_has_payments') setConCobros(p);
      else setProblema(p);
    },
  });

  /*
   * Las devoluciones del club, para saber **cuánto queda por devolver de cada cobro**.
   * Se pide la lista entera y se filtra por `payment_id` porque no hay endpoint por
   * recibo: son pocas por definición —devolver dinero es raro— y el tope del servidor
   * es de 200.
   */
  const devoluciones = useQuery({
    queryKey: ['devoluciones', club],
    enabled: puedeCobrar,
    queryFn: async () => {
      const { data, error } = await api.v1
        .clubs({ club })
        .refunds.get({ query: { limit: 200 } });
      if (error) throw new ErrorDeApi(aProblema(error));
      return data as unknown as Devolucion[];
    },
  });

  /** El cobro que se está devolviendo, con su formulario abierto. */
  const [devolviendo, setDevolviendo] = useState<Cobro | null>(null);
  const [importeADevolver, setImporteADevolver] = useState('');
  /* `motivoDeDevolucion` y no `motivo`: ese ya existe, y es el de anular el recibo. */
  const [motivoDeDevolucion, setMotivoDeDevolucion] = useState('');

  const devolver = useMutation({
    mutationFn: async (cobro: Cobro) => {
      const centimos = aCentimos(importeADevolver);
      const r = await api.v1.clubs({ club }).refunds.post(
        {
          payment_id: cobro.id,
          /*
           * Sin importe se devuelve **lo que queda de ese cobro**, que es el caso
           * normal. Mandar el total calculado aquí sería repetir una cuenta que el
           * servidor ya hace, y con el riesgo de que discrepen.
           */
          ...(importeADevolver.trim() === '' ? {} : { amount_cents: centimos }),
          reason:
            motivoDeDevolucion.trim() === '' ? null : motivoDeDevolucion.trim(),
        },
        // Sale dinero: clave nueva por pulsación, nunca una fija por cobro.
        { headers: { 'idempotency-key': crypto.randomUUID() } }
      );
      if (r.error) throw new ErrorDeApi(aProblema(r.error));
      return r.data;
    },
    onSuccess: () => {
      setDevolviendo(null);
      setImporteADevolver('');
      setMotivoDeDevolucion('');
      void cache.invalidateQueries({ queryKey: ['devoluciones', club] });
      void cache.invalidateQueries({ queryKey: ['recibo', club, reciboId] });
    },
    onError: (e) => setProblema(aProblema(e)),
  });

  const [abriendoPdf, setAbriendoPdf] = useState(false);

  const verPdf = async () => {
    setAbriendoPdf(true);
    const fallo = await abrirDocumento(
      `/v1/clubs/${club}/invoices/${reciboId}/pdf`,
      opcionesDeSesion
    );
    setAbriendoPdf(false);
    if (fallo) setProblema(fallo);
  };

  if (detalle.isPending) {
    return <Cargando filas={6} etiqueta="Cargando el recibo" />;
  }

  if (detalle.isError || !detalle.data) {
    return (
      <PanelError
        problema={aProblema(detalle.error)}
        reintentar={() => void detalle.refetch()}
      />
    );
  }

  const { invoice, lines, payments } = detalle.data;
  const pendiente = pendienteDe(invoice);

  // ── Anotar un cobro ──────────────────────────────────────────────────────
  if (haciendo === 'cobrar') {
    return (
      <form
        className="formulario"
        onSubmit={(e) => {
          e.preventDefault();
          cobrar.mutate();
        }}
      >
        <h1>Anotar un cobro</h1>

        <p className="tenue">
          Recibo de <strong>{invoice.payer_name}</strong>, pendiente{' '}
          <strong>{euros(pendiente)}</strong>.
        </p>

        <div className="rejilla">
          <label>
            Cómo ha pagado
            <select
              value={metodo}
              onChange={(e) => setMetodo(e.target.value as MetodoDeCobro)}
            >
              {METODOS_A_MANO.map((m) => (
                <option key={m.valor} value={m.valor}>
                  {m.etiqueta}
                </option>
              ))}
            </select>
          </label>

          <label>
            Cuándo entró
            {/* La fecha del dinero, no la del tecleo: se anota días después. */}
            <input
              type="date"
              value={cuando}
              onChange={(e) => setCuando(e.target.value)}
            />
          </label>

          <label>
            Referencia
            <input
              maxLength={200}
              placeholder={
                METODOS_A_MANO.find((m) => m.valor === metodo)?.pista ??
                'Opcional'
              }
              value={referencia}
              onChange={(e) => setReferencia(e.target.value)}
            />
          </label>

          {/*
            El importe, escondido detrás de una casilla a propósito: sin él se cobra
            lo pendiente, que es el caso normal, y cada número tecleado a mano es un
            número que puede escribirse mal.
          */}
          <label className="opcion">
            <input
              type="checkbox"
              checked={otraCantidad}
              onChange={(e) => setOtraCantidad(e.target.checked)}
            />
            Ha pagado otra cantidad
          </label>

          {otraCantidad && (
            <label>
              Cuánto ha pagado
              <input
                required
                inputMode="decimal"
                placeholder={euros(pendiente).replace(/[^\d,.]/g, '')}
                value={importe}
                onChange={(e) => setImporte(e.target.value)}
              />
            </label>
          )}
        </div>

        <div className="nota">
          {otraCantidad ? (
            <>
              <strong>Un pago a medias no salda el recibo.</strong> Sigue
              debiendo la diferencia y sigue apareciendo en morosos, que es lo
              correcto. Más de lo pendiente no se acepta.
            </>
          ) : (
            <>
              Se anotará <strong>{euros(pendiente)}</strong>, lo que queda del
              recibo.
            </>
          )}
        </div>

        {problema && <PanelError problema={problema} />}

        <div className="acciones">
          <button type="submit" disabled={cobrar.isPending}>
            {cobrar.isPending ? 'Anotando…' : 'Anotar el cobro'}
          </button>
          <button
            type="button"
            className="btn-plano"
            disabled={cobrar.isPending}
            onClick={() => {
              setHaciendo('nada');
              setProblema(null);
            }}
          >
            Cancelar
          </button>
        </div>
      </form>
    );
  }

  // ── Anular ───────────────────────────────────────────────────────────────
  if (haciendo === 'anular') {
    return (
      <form
        className="hueco hueco-confirmar"
        onSubmit={(e) => {
          e.preventDefault();
          anular.mutate();
        }}
      >
        <h1>¿Anular el recibo de {invoice.payer_name}?</h1>

        <ul className="lista-clara">
          <li>
            <strong>No se borra.</strong> Un recibo borrado es un descuadre
            contable, y lo contable son cuatro años por ley. Queda aquí como
            anulado.
          </li>
          <li>
            <strong>El motivo se guarda.</strong> Dentro de cuatro años alguien
            va a preguntar por qué, y «no aparece» no es una respuesta.
          </li>
        </ul>

        <label>
          Por qué se anula
          <input
            required
            maxLength={300}
            placeholder="Se emitió por error, la familia se dio de baja en agosto…"
            value={motivo}
            onChange={(e) => setMotivo(e.target.value)}
          />
        </label>

        {/*
          `409 invoice_has_payments`: si el dinero entró, hacer desaparecer el recibo
          no lo devuelve. Lo que corresponde es una devolución, y eso es fase 2, así
          que se dice en vez de ofrecer un botón que no existe.
        */}
        {conCobros && (
          <div className="aviso" role="status">
            <strong>{conCobros.title}</strong> {conCobros.detail}
            <p className="tenue">
              Este recibo tiene {euros(invoice.paid_cents)} cobrados. Anularlo
              haría desaparecer el documento y no el dinero. Las devoluciones
              todavía no están en el panel: de momento hay que devolverlo por
              donde entró y dejarlo anotado aquí.
            </p>
          </div>
        )}

        {problema && <PanelError problema={problema} />}

        <div className="hueco-acciones">
          <button
            type="submit"
            className="btn-peligro"
            disabled={anular.isPending}
          >
            {anular.isPending ? 'Anulando…' : 'Sí, anular el recibo'}
          </button>
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setHaciendo('nada');
              setProblema(null);
              setConCobros(null);
            }}
          >
            Dejarlo
          </button>
        </div>
      </form>
    );
  }

  // ── El recibo ────────────────────────────────────────────────────────────
  return (
    <section>
      <div className="titulo-fila">
        <button type="button" className="btn-plano" onClick={alCerrar}>
          ← Recibos
        </button>
        <button
          type="button"
          disabled={abriendoPdf}
          onClick={() => void verPdf()}
        >
          {abriendoPdf ? 'Abriendo…' : 'Ver el PDF'}
        </button>
      </div>

      <h1>
        {invoice.number ?? 'Recibo sin número'}{' '}
        <EstadoDeRecibo recibo={invoice} />
      </h1>

      <dl className="datos">
        <div>
          <dt>Paga</dt>
          <dd>
            <button
              type="button"
              className="enlace-fila"
              onClick={() => alAbrirPagador(invoice.payer_id)}
            >
              {invoice.payer_name}
            </button>
          </dd>
        </div>
        <div>
          <dt>Mes</dt>
          <dd>{etiquetaDePeriodo(invoice.period)}</dd>
        </div>
        <div>
          <dt>Emitido</dt>
          <dd>{fecha(invoice.issue_date)}</dd>
        </div>
        <div>
          <dt>Vence</dt>
          <dd>{fecha(invoice.due_date)}</dd>
        </div>
      </dl>

      <h2 className="seccion">Por qué se cobra esto</h2>

      <div className="tabla-marco">
        <table className="tabla">
          <thead>
            <tr>
              <th>Concepto</th>
              <th className="num">Cantidad</th>
              <th className="num">Importe</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((l) => (
              <tr key={l.id}>
                <td>{l.description}</td>
                <td className="num tenue">
                  {l.quantity === 1 ? '' : l.quantity}
                </td>
                {/* Las líneas de descuento vienen en negativo: se leen como un recibo. */}
                <td
                  className={l.amount_cents < 0 ? 'num desglose-resta' : 'num'}
                >
                  {euros(l.amount_cents)}
                </td>
              </tr>
            ))}
          </tbody>
          <tfoot>
            <tr>
              <th colSpan={2}>Total</th>
              <td className="num">
                <strong>{euros(invoice.total_cents)}</strong>
              </td>
            </tr>
            <tr>
              <th colSpan={2}>Cobrado</th>
              <td className="num">{euros(invoice.paid_cents)}</td>
            </tr>
            <tr>
              <th colSpan={2}>Pendiente</th>
              <td className="num">
                {invoice.status === 'void' ? (
                  <span className="tenue">nada, está anulado</span>
                ) : pendiente <= 0 ? (
                  <span className="tenue">nada</span>
                ) : (
                  <strong>{euros(pendiente)}</strong>
                )}
              </td>
            </tr>
          </tfoot>
        </table>
      </div>

      <p className="tenue">
        Lo cobrado es <strong>la suma de los cobros de abajo</strong>, no una
        cifra guardada aparte: no pueden discrepar.
      </p>

      <h2 className="seccion">Cobros</h2>

      {payments.length === 0 ? (
        <p className="tenue">Todavía no se ha cobrado nada de este recibo.</p>
      ) : (
        <ul className="cobros">
          {payments.map((c) => {
            const queda = quedaPorDevolver(c, devoluciones.data);
            const devuelto = queda !== null && queda < c.amount_cents;

            return (
              <li key={c.id}>
                <span>
                  <strong>{euros(c.amount_cents)}</strong>{' '}
                  <span className="tenue">
                    · {etiquetaDeMetodo(c.method)}
                    {c.reference ? ` · ${c.reference}` : ''}
                  </span>
                  {/*
                    Lo devuelto de **este** cobro, no del recibo: es lo que evita
                    devolver dos veces el mismo Bizum.
                  */}
                  {devuelto && (
                    <span className="tenue">
                      {queda === 0
                        ? ' · devuelto entero'
                        : ` · devueltos ${euros(c.amount_cents - queda)}`}
                    </span>
                  )}
                </span>

                <span className="cobro-derecha">
                  <span className="tenue">
                    {fechaYHora(c.settled_at ?? c.created_at)}
                  </span>
                  {/*
                    Solo un cobro confirmado se devuelve, y solo si queda algo: de un
                    cobro que falló no salió dinero, y devolver de más responde `422`.
                  */}
                  {puedeCobrar &&
                    sePuedeDevolver(c) &&
                    queda !== 0 &&
                    devolviendo?.id !== c.id && (
                      <button
                        type="button"
                        className="btn-plano"
                        onClick={() => {
                          setDevolviendo(c);
                          setImporteADevolver('');
                          setMotivoDeDevolucion('');
                          setProblema(null);
                        }}
                      >
                        Devolver
                      </button>
                    )}
                </span>

                {devolviendo?.id === c.id && (
                  <form
                    className="devolver"
                    onSubmit={(e) => {
                      e.preventDefault();
                      setProblema(null);
                      devolver.mutate(c);
                    }}
                  >
                    <div className="rejilla">
                      <label>
                        Cuánto devolver
                        <input
                          inputMode="decimal"
                          placeholder={
                            queda === null
                              ? 'Todo lo que queda'
                              : `Todo: ${euros(queda)}`
                          }
                          value={importeADevolver}
                          onChange={(e) => setImporteADevolver(e.target.value)}
                        />
                      </label>

                      <label>
                        Por qué
                        <input
                          maxLength={500}
                          placeholder="Se dio de baja el día 5"
                          value={motivoDeDevolucion}
                          onChange={(e) =>
                            setMotivoDeDevolucion(e.target.value)
                          }
                        />
                      </label>
                    </div>

                    <p className="tenue">
                      En blanco devuelve lo que queda de este cobro.{' '}
                      <strong>Kiap no mueve el dinero</strong>: esto es el
                      apunte, y la transferencia o el Bizum los haces tú.
                    </p>

                    <div className="acciones">
                      <button type="submit" disabled={devolver.isPending}>
                        {devolver.isPending
                          ? 'Anotando…'
                          : 'Anotar la devolución'}
                      </button>
                      <button
                        type="button"
                        className="btn-plano"
                        disabled={devolver.isPending}
                        onClick={() => {
                          setDevolviendo(null);
                          setProblema(null);
                        }}
                      >
                        Cancelar
                      </button>
                    </div>
                  </form>
                )}
              </li>
            );
          })}
        </ul>
      )}

      {problema && <PanelError problema={problema} />}

      <div className="acciones">
        {puedeCobrar && pendiente > 0 && invoice.status !== 'void' && (
          <button
            type="button"
            onClick={() => {
              setHaciendo('cobrar');
              setProblema(null);
            }}
          >
            Anotar un cobro
          </button>
        )}

        {puedeCobrar && invoice.status !== 'void' && (
          <button
            type="button"
            className="btn-plano"
            onClick={() => {
              setHaciendo('anular');
              setProblema(null);
              setConCobros(null);
            }}
          >
            Anular el recibo
          </button>
        )}
      </div>
    </section>
  );
}
